/** @file Moves keys of one timeline by whole frames; a moved key replaces the key it lands on. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { timelineKeys, type FramedKey } from './timelineKeys';

/** animation.moveKeys: new frame = frame + delta; the timeline is rebuilt in frame order, never spliced. */
export const command = defineCommand({
  name: 'animation.moveKeys',
  description: 'Move the keys at `frames` of one timeline (channel + target as in deleteKeys) by `delta` frames; a moved key replaces an unmoved key on its new frame (for events: the key of the same event), keys of other events stack after the ones already there, in their order.',
  input: z
    .object({
      animation: z.string().min(1),
      channel: timelineKeys.channel,
      target: z.string().min(1).exactOptional(),
      frames: z.array(prim.frame).min(1),
      delta: z.number().int(),
    })
    .strict(),
  apply(draft, input, ctx) {
    const ref = timelineKeys.resolve(draft, input, ctx);
    const pick = new Set(input.frames);
    const moving = ref.keys.filter((k) => pick.has(k.f));
    if (moving.length === 0) {
      ctx.warnings.push({ code: 'INFO', message: 'no keys matched; nothing was moved', path: 'frames' });
      return;
    }
    if (input.delta === 0) return;
    if (moving.some((k) => k.f + input.delta < 0)) throwError('VALIDATION', undefined, { path: 'delta', hint: 'keys would move before frame 0' });
    // A moved key replaces the unmoved key of its lane on its new frame; event keys of other events stack (m3).
    const spot = (k: FramedKey, f: number): string => `${f}:${ref.lane(k)}`;
    const landing = new Set(moving.map((k) => spot(k, k.f + input.delta)));
    const staying = ref.keys.filter((k) => !pick.has(k.f) && !landing.has(spot(k, k.f)));
    const replaced = ref.keys.length - moving.length - staying.length;
    for (const k of moving) k.f += input.delta;
    ref.write([...staying, ...moving].sort((a, b) => a.f - b.f));
    if (replaced > 0) ctx.warnings.push({ code: 'INFO', message: `${replaced} keys were replaced by moved keys`, params: { replaced }, path: ref.path });
    draft.meta.updatedAt = ctx.now;
  },
});
